import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import EventStartDatePicker from "components/EventStartDatePicker";
import EventEndDatePicker from "components/EventEndDatePicker";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
} from "@mui/material";
import SelectMultipleFacultyType from "./SelectMultipleFacultyType";
import { CourseDocumentFilter, CourseStatus } from "services/facultyPortalClient";
import { CommonFilter, useIsFilterApplied } from "@knowfully/ui.common";

const styles = {
  label: {
    transform: "translate(0, -1.5rem) scale(1)",
    fontSize: 12,
  },
  checkbox: {
    "& .MuiSvgIcon-root": {
      fontSize: 24,
    },
  },
};

type CourseDocumentsFilterProps = {
  onChange: (filter: CourseDocumentFilter) => void;
  courseStatus?: CourseStatus;
};

export default function CourseDocumentsFilter({
  onChange,
  courseStatus,
}: CourseDocumentsFilterProps) {
  const { formatMessage: t } = useIntl();
  const [filter, setFilter] = useState({} as CourseDocumentFilter);

  const isFilterApplied = useIsFilterApplied(filter, []);

  const onChangeInternal = (prop: keyof CourseDocumentFilter, val: any) => {
    setFilter((prev) => ({ ...prev, [prop]: val }) as CourseDocumentFilter);
  };

  // Auto-apply filters to parent on any change
  useEffect(() => {
    onChange(filter);
  }, [filter, onChange]);

  const clearFilter = () => {
    const emptyFilter = {} as CourseDocumentFilter;
    setFilter(emptyFilter);
    onChange(emptyFilter);
  };

  return (
    <CommonFilter
      searchText={filter.searchText}
      onSearchTextChange={(val) => onChangeInternal("searchText", val)}
      onClear={clearFilter}
      isFilterApplied={isFilterApplied}
    >
      <Grid item xs={12} sm={12} md={12} />
      <Grid item xs={12} sm={12} md={6}>
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel shrink sx={styles.label}>
            {t({ id: "documentDate" })}
          </InputLabel>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6}>
              <EventStartDatePicker
                value={filter.eventStartDate}
                onChange={(value) => onChangeInternal("eventStartDate", value)}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <EventEndDatePicker
                value={filter.eventEndDate}
                onChange={(value) => onChangeInternal("eventEndDate", value)}
              />
            </Grid>
          </Grid>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={12} lg={3} md={6}>
        <SelectMultipleFacultyType
          value={filter.resourceTypeIds}
          onChange={(value) => onChangeInternal("resourceTypeIds", value)}
          courseStatus={courseStatus}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12}>
        <FormControlLabel
          label={t({ id: "showOnlyDocsRequireAttention" })}
          control={
            <Checkbox
              checked={!!filter.onlyAttentionRequiredCourses}
              size="small"
              sx={styles.checkbox}
              onChange={(e) =>
                onChangeInternal(
                  "onlyAttentionRequiredCourses",
                  e.target.checked
                )
              }
            />
          }
        />
      </Grid>
    </CommonFilter>
  );
}
